import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import FiltersSidebar from './FiltersSidebar';
import JobList from './JobList';
import useFetch from '../../useFetch';
import Seo from '../Common/Seo';

const emptyFilters = {
  keyword: '',
  location: '',
  category: '',
  experience: '',
  salaryMin: '',
  salaryMax: '',
  jobTypes: [],
  datePosted: '',
  sort: 'newest',
};

const JobListings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchString = searchParams.toString();
  const urlFilters = useMemo(() => {
    const params = new URLSearchParams(searchString);

    return {
      keyword: params.get('keyword') ?? '',
      location: params.get('location') ?? '',
      category: params.get('category') ?? '',
      experience: params.get('experience') ?? '',
      salaryMin: params.get('salary_min') ?? '',
      salaryMax: params.get('salary_max') ?? '',
      jobTypes: params.get('job_types') ? params.get('job_types').split(',') : [],
      datePosted: params.get('date_posted') ?? '',
      sort: params.get('sort') ?? 'newest',
      page: Math.max(Number(params.get('page') ?? 1), 1),
    };
  }, [searchString]);
  const [filters, setFilters] = useState(urlFilters);
  const { response: optionsResponse } = useFetch(process.env.REACT_APP_BASE_API_URL + '/jobs/filter-options');
  const filterOptions = optionsResponse?.filters ?? {};

  useEffect(() => {
    setFilters(urlFilters);
  }, [urlFilters]);

  const updateUrl = (nextFilters) => {
    const params = new URLSearchParams();

    if (nextFilters.keyword) params.set('keyword', nextFilters.keyword);
    if (nextFilters.location) params.set('location', nextFilters.location);
    if (nextFilters.category) params.set('category', nextFilters.category);
    if (nextFilters.experience) params.set('experience', nextFilters.experience);
    if (nextFilters.salaryMin) params.set('salary_min', nextFilters.salaryMin);
    if (nextFilters.salaryMax) params.set('salary_max', nextFilters.salaryMax);
    if (nextFilters.jobTypes.length > 0) params.set('job_types', nextFilters.jobTypes.join(','));
    if (nextFilters.datePosted) params.set('date_posted', nextFilters.datePosted);
    if (nextFilters.sort && nextFilters.sort !== 'newest') params.set('sort', nextFilters.sort);
    if (nextFilters.page && nextFilters.page > 1) params.set('page', nextFilters.page);

    setSearchParams(params);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const field = name === 'date_posted' ? 'datePosted' : name;
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleJobTypeChange = (type, checked) => {
    setFilters((current) => ({
      ...current,
      jobTypes: checked ? [...current.jobTypes, type] : current.jobTypes.filter((item) => item !== type),
    }));
  };

  const submitFilters = (e) => {
    e.preventDefault();
    updateUrl({ ...filters, page: 1 });
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setSearchParams({});
  };

  return (
    <>
      <Seo
        title="Find Jobs | Epic-Jobs"
        description="Search active jobs by keyword, location, category, salary range, experience, and job type on Epic-Jobs."
      />
      <section className="page-title">
        <div className="auto-container">
          <div className="title-outer">
            <h1>Find Jobs</h1>
            <ul className="page-breadcrumb">
              <li><Link to="/">Home</Link></li>
              <li>Jobs</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="ls-section">
        <div className="auto-container">
          <div className="filters-backdrop"></div>

          <div className="row">
            <div className="filters-column col-lg-4 col-md-12 col-sm-12">
              <FiltersSidebar
                filters={filters}
                filterOptions={filterOptions}
                onChange={handleChange}
                onJobTypeChange={handleJobTypeChange}
                onSubmit={submitFilters}
                onReset={resetFilters}
              />
            </div>

            <div className="content-column col-lg-8 col-md-12 col-sm-12">
              <JobList
                filters={urlFilters}
                page={urlFilters.page}
                onPageChange={(page) => updateUrl({ ...urlFilters, page })}
                onSortChange={(sort) => updateUrl({ ...urlFilters, sort, page: 1 })}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default JobListings;
